import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { PriceBreakdown, PricePreviewArgs } from './pricePreviewApiTypes';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

// Booking price single source of truth (#1203). Calls the new (ECS) backend
// POST /admin/order/price-preview — the base URL already carries the /admin
// prefix, and newBackendAxiosBaseQuery attaches the X-Tenant header. Modeled as
// a query keyed on the serializable pricing inputs so RTK Query dedups/caches
// identical arg sets during live editing.
export const pricePreviewApiSlice = createApi({
    reducerPath: 'pricePreviewApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    endpoints: (builder) => ({
        getPricePreview: builder.query<PriceBreakdown, PricePreviewArgs>({
            query: (body) => ({
                url: '/order/price-preview',
                method: 'POST',
                data: body,
            }),
        }),
        // #1203: the price of a SAVED booking. The view surfaces have no form to
        // build a preview request from — they send the booking id and the backend
        // resolves the rest, returning what the booking is actually billed at.
        // Same endpoint, so view/create/edit all render one computation.
        getSavedBookingPrice: builder.query<PriceBreakdown, { orderId: string }>({
            query: ({ orderId }) => ({
                url: '/order/price-preview',
                method: 'POST',
                data: { orderId },
            }),
            // A price must never outlive the edit that changed it. This entry is
            // keyed on the order id alone — an argument that never changes — so
            // without both of these the detail page would serve the pre-edit
            // breakdown for as long as the tab stays open. #1906 predicted this
            // ("a cached preview can outlive the mutation that changed its
            // inputs") while no surface was keyed that way; the detail view is.
            //
            // Dropped the moment nothing renders it, so returning from the edit
            // screen always refetches; `useSavedBookingPrice` additionally
            // refetches on mount, and in-place mutations invalidate the tag.
            keepUnusedDataFor: 0,
            providesTags: ['savedBookingPrice'],
        }),
    }),
    tagTypes: ['savedBookingPrice'],
});

export const {
    useGetPricePreviewQuery,
    useLazyGetPricePreviewQuery,
    useGetSavedBookingPriceQuery,
} = pricePreviewApiSlice;

/**
 * Drop the cached price of every saved booking. Dispatch after any mutation that
 * can move a total — the edit lives in another api slice, so it cannot invalidate
 * this tag itself.
 */
export const invalidateSavedBookingPrice = () =>
    pricePreviewApiSlice.util.invalidateTags(['savedBookingPrice']);
